/**
 * K108-Z — app-side Companion token-rehydrate bridge.
 *
 * After the Companion's service worker restarts, it restores its monitor from
 * non-sensitive state but has NO token (the token is memory-only). Its content
 * script asks THIS page (same-origin `window.postMessage`) to mint a fresh token
 * for the SAME session; we mint with resume:true (no new session row — the mint
 * edge function enforces ownership + mintable status) and hand it back through the
 * existing secure Companion handoff. The token is never stored, logged, or put in a
 * URL. Renders nothing.
 */
import { useEffect } from 'react';
import { useTrainingSessionToken } from '@/hooks/useTrainingSessionToken';
import { emitCompanionTokenHandoff } from '@/lib/trainingCompanion/companionTokenHandoff';

const REHYDRATE_REQUEST_MESSAGE_TYPE = 'OPTIBAY_TRAINING_COMPANION_REHYDRATE_REQUEST';

export function CompanionRehydrateBridge() {
  const mint = useTrainingSessionToken();

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      // Same-window only (rejects cross-window/origin messages).
      if (event.source !== window) return;
      const data = event.data as { type?: unknown; sessionId?: unknown } | null;
      if (!data || typeof data !== 'object') return;
      if (data.type !== REHYDRATE_REQUEST_MESSAGE_TYPE) return;
      if (typeof data.sessionId !== 'string' || !data.sessionId) return;
      const sessionId = data.sessionId;
      void (async () => {
        try {
          // resume:true rotates the token for the EXISTING session (no new row).
          // The mint edge function still enforces ownership + mintable status; a
          // logged-out / non-owner / non-mintable request is refused and the
          // Companion simply stays paused ("return to OptiBay to reconnect").
          const result = await mint.mutateAsync({ sessionId, resume: true });
          await emitCompanionTokenHandoff({
            sessionId: result.sessionId,
            token: result.token,
            expiresAt: result.expiresAt,
            rehydrate: true, // reconnect the restored monitor; do NOT open a new tab
          });
        } catch {
          // Mint failed (logged out / not mintable) — Companion remains paused and
          // the content-script poll will retry.
        }
      })();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [mint]);

  return null;
}
